import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Col,
  Form,
  ListGroup,
  Nav,
  Row,
  Spinner
} from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ProviderKey } from '@/app/Settings/types/aiSettingsApiTypes';
import SubtleBadge from '@/components/common/SubtleBadge';
import { toPersianDigit } from '@/helpers/utils';
import type { useMcpChatOnboardingGate } from '../../hooks/useMcpChatOnboardingGate';
import type { McpChatOnboardingStepId } from '../../lib/mcp-chat-onboarding';

type McpChatOnboardingGate = ReturnType<typeof useMcpChatOnboardingGate>;

const AI_SETTINGS_PATH = '/settings/ai';
const AI_WALLET_CHARGE_PATH = '/prorogate-or-upgrade/?wallet=500000';

/** Daily free chat responses with مدل Auto — keep in sync with product policy. */
const CHAT_FREE_RESPONSES_PROFESSIONAL = 15;
const CHAT_FREE_RESPONSES_STANDARD = 5;

function formatToman(amount: number): string {
  return `${toPersianDigit(amount.toLocaleString('en-US'))} تومان`;
}

function pd(n: number): string {
  return toPersianDigit(String(n));
}

function ConsentTermsBody() {
  const proBase = CHAT_FREE_RESPONSES_PROFESSIONAL;
  const stdBase = CHAT_FREE_RESPONSES_STANDARD;
  const proWithKey = proBase * 2;
  const stdWithKey = stdBase * 2;

  return (
    <div className="fs--1 text-700 mb-4 mcp-chat-onboarding-consent-terms">
      <div className="mb-3">
        <h6 className="text-800 fw-semi-bold mb-2">
          ۱. بودجه رایگان میزفا تولز (مدل Auto)
        </h6>
        <p className="mb-2">
          با انتخاب <span className="text-800">مدل Auto</span> از بخش انتخاب
          مدل‌ها، به‌ازای هر پاسخ دستیار، یک واحد از بودجه رایگان روزانه شما
          مصرف می‌شود.
        </p>
        <p className="mb-2">
          سقف استفاده روزانه: {pd(proBase)} پاسخ برای طرح حرفه‌ای و{' '}
          {pd(stdBase)} پاسخ برای سایر طرح‌ها.
        </p>
        <p className="mb-2">
          با ثبت و فعال‌سازی کلید API شخصی در{' '}
          <Link to={AI_SETTINGS_PATH}>تنظیمات AI</Link>، سقف روزانه استفاده از
          مدل Auto دو برابر می‌شود؛ یعنی {pd(proWithKey)} پاسخ برای طرح
          حرفه‌ای و {pd(stdWithKey)} پاسخ برای سایر طرح‌ها.
        </p>
        <p className="mb-0">
          اگر بودجه رایگان روزانه تمام شود، گفتگو قطع نمی‌شود؛ به‌ازای هر
          پاسخ بعدی، هزینه از اعتبار AI کیف پول شما کسر می‌شود.
        </p>
      </div>

      {/* Temporarily hidden: personal Gemini API key path
      <div className="mb-3">
        <h6 className="text-800 fw-semi-bold mb-2">
          ۲. استفاده از کلید API شخصی با مدل Gemini
        </h6>
        <p className="mb-2">
          اگر کلید API شخصی خود را در{' '}
          <Link to={AI_SETTINGS_PATH}>تنظیمات AI</Link> ثبت و فعال کرده باشید،
          می‌توانید به‌جای مدل Auto، یکی از مدل‌های Gemini را انتخاب کنید.
        </p>
        <p className="mb-0">
          در این حالت، هزینه استفاده از چت از سهمیه و بودجه روزانه کلید API
          شخصی شما در سرویس Google تأمین می‌شود و استفاده از چت برای شما در
          میزفا تولز رایگان خواهد بود؛ بنابراین، بودجه رایگان روزانه میزفا تولز
          مصرف نمی‌شود.
        </p>
      </div>
      */}

      <div className="mb-0">
        <h6 className="text-800 fw-semi-bold mb-2">
          ۲. ادامه گفتگو هنگام اختلال یا محدودیت مدل‌ها
        </h6>
        <p className="mb-2">
          اگر مدل Auto موقتاً در دسترس نباشد یا امکان پاسخ‌گویی با آن وجود
          نداشته باشد، ممکن است برای ادامه گفتگو از مدل‌های دیگر استفاده شود.
        </p>
        <p className="mb-0">
          در این شرایط، هزینه پاسخ‌ها از اعتبار AI کیف پول شما کسر می‌شود؛
          البته فقط در صورتی که اعتبار AI کافی داشته باشید.
        </p>
      </div>
    </div>
  );
}

const STEPPER_ACCENT =
  'var(--falcon-navbar-vertical-default-link-active-color)';

function isStepReached(
  stepId: McpChatOnboardingStepId,
  currentStep: McpChatOnboardingStepId,
  visitedSteps: Record<McpChatOnboardingStepId, boolean>
): boolean {
  return stepId < currentStep || Boolean(visitedSteps[stepId]);
}

function Stepper({
  currentStep,
  stepStatus,
  visitedSteps,
  onSelectStep
}: {
  currentStep: McpChatOnboardingStepId;
  stepStatus: Record<McpChatOnboardingStepId, boolean>;
  visitedSteps: Record<McpChatOnboardingStepId, boolean>;
  onSelectStep: (step: McpChatOnboardingStepId) => void;
}) {
  const steps: Array<{ id: McpChatOnboardingStepId; label: string }> = [
    { id: 1, label: 'شرایط استفاده' },
    { id: 2, label: 'کلید API' },
    { id: 3, label: 'کیف پول AI' }
  ];

  return (
    <>
      <style>{`
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link.active:not(.nav-pills .active) {
          color: ${STEPPER_ACCENT};
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link.active .nav-item-circle,
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link.done .nav-item-circle {
          background-color: ${STEPPER_ACCENT} !important;
          border-color: ${STEPPER_ACCENT} !important;
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link.done {
          color: ${STEPPER_ACCENT};
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link.done .nav-item-circle-parent:before {
          background-color: ${STEPPER_ACCENT};
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-item:not(:first-child) .active .nav-item-circle-parent:after {
          background-color: transparent;
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-item:not(:first-child) .active.prev-complete .nav-item-circle-parent:after {
          background-color: ${STEPPER_ACCENT};
        }
        .mcp-chat-onboarding-wizard.theme-wizard .nav-link {
          cursor: pointer;
        }
      `}</style>
      <div
        className="theme-wizard mcp-chat-onboarding-wizard mb-4 border rounded bg-body-tertiary px-1 py-2"
      >
        <Nav className="justify-content-center flex-nowrap">
          {steps.map(step => {
            const active = currentStep === step.id;
            const isComplete = Boolean(
              stepStatus[step.id] &&
                isStepReached(step.id, currentStep, visitedSteps)
            );
            const done = isComplete && !active;
            const prevId =
              step.id > 1
                ? ((step.id - 1) as McpChatOnboardingStepId)
                : null;
            const prevComplete =
              prevId != null &&
              Boolean(stepStatus[prevId]) &&
              isStepReached(prevId, currentStep, visitedSteps);

            return (
              <Nav.Item key={step.id}>
                <Nav.Link
                  className={classNames('fw-semibold', {
                    done,
                    active,
                    'prev-complete': active && prevComplete
                  })}
                  onClick={() => onSelectStep(step.id)}
                >
                  <span className="nav-item-circle-parent p-0 p-md-1">
                    {isComplete ? (
                      <span className="nav-item-circle">
                        <FontAwesomeIcon icon="check" />
                      </span>
                    ) : (
                      <span className="nav-item-circle">
                        {toPersianDigit(String(step.id))}
                      </span>
                    )}
                  </span>
                  <span className="d-none d-md-block mt-1 fs--1">
                    {step.label}
                  </span>
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>
      </div>
    </>
  );
}

function StepFooter({ children }: { children: ReactNode }) {
  return (
    <div
      className="mcp-chat-onboarding-step-footer border-top d-flex justify-content-end flex-wrap gap-2 pt-3 mt-3"
    >
      {children}
    </div>
  );
}

function StepHeader({ title }: { title: string }) {
  return (
    <h5 className="mb-3">{title}</h5>
  );
}

function ConsentStep({
  accepted,
  onAccept,
  onSkip
}: {
  accepted: boolean;
  onAccept: () => void | Promise<void>;
  onSkip?: () => void;
}) {
  const [checked, setChecked] = useState(accepted);
  const [isSaving, setIsSaving] = useState(false);

  return (
    <div className="mcp-chat-onboarding-step">
      <StepHeader title="شرایط استفاده از چت" />

      {accepted ? (
        <Alert variant="success" className="fs--1 mb-0">
          شرایط استفاده را پذیرفته‌اید. می‌توانید این مرحله را رد کنید و به
          مراحل بعدی بروید.
        </Alert>
      ) : (
        <>
          <ConsentTermsBody />

          <Form.Check
            type="checkbox"
            id="mcp-chat-onboarding-consent"
            className="mb-0"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            label="شرایط بالا را خواندم و می‌پذیرم."
          />
        </>
      )}

      <StepFooter>
        {accepted && onSkip ? (
          <Button variant="falcon-primary" size="sm" onClick={onSkip}>
            رد کردن این مرحله
          </Button>
        ) : (
          <Button
            variant="falcon-primary"
            size="sm"
            disabled={!checked || isSaving}
            onClick={async () => {
              setIsSaving(true);
              try {
                await onAccept();
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? 'در حال ذخیره...' : 'تأیید و ادامه'}
          </Button>
        )}
      </StepFooter>
    </div>
  );
}

function ApiKeyBenefitNote() {
  const proBase = CHAT_FREE_RESPONSES_PROFESSIONAL;
  const stdBase = CHAT_FREE_RESPONSES_STANDARD;

  return (
    <Alert variant="info" className="fs--1 mb-3">
      با وارد کردن و فعال‌سازی کلید API شخصی، بودجه رایگان روزانه مدل Auto دو
      برابر می‌شود؛ یعنی از {pd(proBase)} به {pd(proBase * 2)} پاسخ در طرح
      حرفه‌ای و از {pd(stdBase)} به {pd(stdBase * 2)} پاسخ در سایر طرح‌ها.
      وارد کردن کلید اختیاری است و می‌توانید این مرحله را رد کنید.
    </Alert>
  );
}

function ApiKeyStep({
  hasApiKeyReady,
  hasActiveApiKey,
  enableUserAi,
  activeProviderKeys,
  onRefresh,
  isRefreshing,
  onSkip
}: {
  hasApiKeyReady: boolean;
  hasActiveApiKey: boolean;
  enableUserAi: boolean;
  activeProviderKeys: ProviderKey[];
  onRefresh: () => void;
  isRefreshing: boolean;
  onSkip?: () => void;
}) {
  const guidance = useMemo(() => {
    if (hasApiKeyReady) return null;
    if (!hasActiveApiKey) {
      return (
        <>
          هنوز کلید API فعالی ثبت نشده است. به{' '}
          <Link to={AI_SETTINGS_PATH} className="alert-link">
            تنظیمات AI
          </Link>{' '}
          بروید، یک کلید Gemini اضافه کنید و وضعیت آن را روی «فعال» بگذارید.
        </>
      );
    }
    if (!enableUserAi) {
      return (
        <>
          کلید فعال دارید، اما حالت «کلید API شخصی» روشن نیست. در{' '}
          <Link to={AI_SETTINGS_PATH} className="alert-link">
            تنظیمات AI
          </Link>{' '}
          استفاده از کلید شخصی را فعال کنید.
        </>
      );
    }
    return (
      <>
        کلید ثبت شده، اما هنوز تأیید نشده است. روی «بررسی مجدد» بزنید تا معتبر
        بودن آن را چک کنیم.
      </>
    );
  }, [enableUserAi, hasActiveApiKey, hasApiKeyReady]);

  return (
    <div className="mcp-chat-onboarding-step">
      <StepHeader title="ثبت کلید API" />

      {hasApiKeyReady ? (
        <Alert variant="success" className="fs--1 mb-3">
          کلید API و حالت کلید شخصی آماده است. بودجه رایگان روزانه مدل Auto
          برای شما دو برابر شده است.
        </Alert>
      ) : (
        <>
          <ApiKeyBenefitNote />
          <Alert variant="warning" className="fs--1 mb-3">
            {guidance}
          </Alert>
        </>
      )}

      <div className="fs--1 mb-1 fw-semi-bold">کلیدهای فعال</div>
      {activeProviderKeys.length === 0 ? (
        <p className="fs--1 text-600 mb-0">هیچ کلید فعالی یافت نشد.</p>
      ) : (
        <ListGroup variant="flush" className="border rounded">
          {activeProviderKeys.map(key => {
            const verified = Boolean(key.verified);
            const statusLabel = verified ? 'تأییدشده' : 'تأییدنشده';
            const badgeTitle =
              !verified && key.last_error
                ? `${statusLabel}: ${key.last_error}`
                : statusLabel;

            return (
              <ListGroup.Item
                key={key.id}
                className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-1 py-2 px-3"
              >
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 min-w-0">
                    <div className="fw-semi-bold text-truncate min-w-0">
                      {key.key_name || 'بدون نام'}
                    </div>
                    <span title={badgeTitle} className="flex-shrink-0">
                      <SubtleBadge
                        bg={verified ? 'success' : 'warning'}
                        pill
                        className="fs--2 fw-normal"
                        style={{ lineHeight: 1.25 }}
                      >
                        {statusLabel}
                      </SubtleBadge>
                    </span>
                  </div>
                  <div className="text-600 fs--2 mt-1">
                    {key.provider}
                    {key.model ? ` · ${key.model}` : ''}
                  </div>
                </div>
                <code className="fs--2 text-truncate">
                  {key.encrypted_api_key}
                </code>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {!enableUserAi && hasActiveApiKey ? (
        <p className="fs--1 text-warning mb-0 mt-2">
          برای استفاده از این کلیدها، حالت کلید شخصی را در تنظیمات روشن کنید.
        </p>
      ) : null}

      <p className="fs--1 mb-0 mt-3">
        <Link to={AI_SETTINGS_PATH}>رفتن به تنظیمات AI</Link>
      </p>

      <StepFooter>
        {onSkip ? (
          <Button
            variant={hasApiKeyReady ? 'falcon-primary' : 'falcon-default'}
            size="sm"
            onClick={onSkip}
          >
            رد کردن این مرحله
          </Button>
        ) : null}
        {!hasApiKeyReady ? (
          <Button
            variant="falcon-primary"
            size="sm"
            disabled={isRefreshing}
            onClick={onRefresh}
          >
            {isRefreshing ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-1"
                  style={{ width: 14, height: 14 }}
                />
                در حال بررسی...
              </>
            ) : (
              'بررسی مجدد'
            )}
          </Button>
        ) : null}
      </StepFooter>
    </div>
  );
}

function WalletStep({
  hasAiWalletBalance,
  aiBalance,
  domainSlug,
  canEnterChat,
  onEnterChat
}: {
  hasAiWalletBalance: boolean;
  aiBalance: number;
  domainSlug: string | null;
  canEnterChat: boolean;
  onEnterChat: () => void | Promise<void>;
}) {
  const [isEntering, setIsEntering] = useState(false);

  return (
    <div className="mcp-chat-onboarding-step">
      <StepHeader title="شارژ اعتبار AI" />

      <div className="border rounded px-3 py-3 mb-3 bg-body-tertiary d-flex align-items-center gap-3">
        <span className="fa-stack flex-shrink-0">
          <FontAwesomeIcon
            icon="circle"
            className="fa-stack-2x text-primary opacity-25"
          />
          <FontAwesomeIcon icon="wallet" className="fa-stack-1x text-primary" />
        </span>
        <div className="min-w-0">
          <div className="fs--1 text-600 mb-1">موجودی کیف پول AI</div>
          <div className="fs-1 fw-semi-bold">
            {domainSlug ? formatToman(aiBalance) : '—'}
          </div>
        </div>
      </div>

      {!domainSlug ? (
        <Alert variant="warning" className="fs--1 mb-0">
          ابتدا یک دامنه در پنل انتخاب کنید تا موجودی کیف پول AI بررسی شود.
        </Alert>
      ) : hasAiWalletBalance ? (
        <Alert variant="success" className="fs--1 mb-0">
          اعتبار AI شما کافی است و این مرحله تکمیل شده.
        </Alert>
      ) : (
        <Alert variant="warning" className="fs--1 mb-0">
          اعتبار AI ندارید. بهتر است شارژ کنید تا اگر مدل Auto موقتاً در دسترس
          نبود، گفتگو قطع نشود و با مدل‌های دیگر ادامه پیدا کند.
        </Alert>
      )}

      <StepFooter>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {!hasAiWalletBalance && domainSlug ? (
            <Link
              to={AI_WALLET_CHARGE_PATH}
              className="btn btn-sm btn-falcon-default"
            >
              شارژ اعتبار AI
            </Link>
          ) : null}
          <Button
            variant="falcon-primary"
            size="sm"
            disabled={!canEnterChat || isEntering}
            onClick={async () => {
              setIsEntering(true);
              try {
                await onEnterChat();
              } finally {
                setIsEntering(false);
              }
            }}
          >
            {isEntering ? 'در حال ورود...' : 'ورود به چت'}
          </Button>
        </div>
      </StepFooter>
    </div>
  );
}

type McpChatOnboardingProps = {
  gate: McpChatOnboardingGate;
  variant?: 'page' | 'modal';
  /** When false, completing step 3 only marks onboarding done (modal flow on /chat). */
  onEnterChatNavigate?: boolean;
};

const McpChatOnboarding = ({
  gate,
  variant = 'page',
  onEnterChatNavigate = true
}: McpChatOnboardingProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [focusedStep, setFocusedStep] = useState<McpChatOnboardingStepId | null>(
    null
  );
  const [visitedSteps, setVisitedSteps] = useState<
    Partial<Record<McpChatOnboardingStepId, boolean>>
  >({});
  const autoVerifyKey = useRef<string | null>(null);
  const advanceOnKeyReady = useRef(false);

  const fallbackStep =
    gate.lastSeenStep === 2 && gate.currentStep === 3
      ? 2
      : gate.currentStep;
  const activeStep = focusedStep ?? fallbackStep;
  const markSeenStep = gate.markSeenStep;

  useEffect(() => {
    setVisitedSteps(prev => {
      const next: Partial<Record<McpChatOnboardingStepId, boolean>> = {
        ...prev,
        [activeStep]: true
      };
      const lastSeen = gate.lastSeenStep;
      if (activeStep > 1 || lastSeen >= 1) next[1] = true;
      if (activeStep > 2 || lastSeen >= 2) next[2] = true;
      if (lastSeen >= 3) next[3] = true;
      if (
        prev[1] === next[1] &&
        prev[2] === next[2] &&
        prev[3] === next[3]
      ) {
        return prev;
      }
      return next;
    });
    markSeenStep?.(activeStep);
  }, [activeStep, gate.lastSeenStep, markSeenStep]);

  useEffect(() => {
    if (activeStep !== 2) return;
    if (!gate.enableUserAi) return;
    const unverifiedIds = gate.activeProviderKeys
      .filter(key => !key.verified)
      .map(key => key.id)
      .join(',');
    if (!unverifiedIds) return;
    if (autoVerifyKey.current === unverifiedIds) return;
    autoVerifyKey.current = unverifiedIds;
    void gate.verifyAndRefresh();
  }, [
    activeStep,
    gate.activeProviderKeys,
    gate.enableUserAi,
    gate.verifyAndRefresh
  ]);

  // After a manual «بررسی مجدد», move on to the wallet step once the key is ready.
  useEffect(() => {
    if (activeStep !== 2) {
      advanceOnKeyReady.current = false;
      return;
    }
    if (!advanceOnKeyReady.current || !gate.hasApiKeyReady) return;
    advanceOnKeyReady.current = false;
    setFocusedStep(3);
  }, [activeStep, gate.hasApiKeyReady]);

  const focusStep = (step: McpChatOnboardingStepId) => {
    setFocusedStep(step);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    advanceOnKeyReady.current = true;
    try {
      autoVerifyKey.current = null;
      await gate.verifyAndRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const goToNextIncomplete = (fromStep: McpChatOnboardingStepId) => {
    if (fromStep === 1 && !gate.stepStatus[2]) {
      focusStep(2);
      return;
    }
    if (fromStep === 1 || fromStep === 2) {
      focusStep(3);
      return;
    }
    setFocusedStep(null);
  };

  if (
    gate.isLoading &&
    !gate.consentAccepted &&
    gate.activeProviderKeys.length === 0
  ) {
    return (
      <div className="d-flex align-items-center justify-content-center py-6">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  const content = (
    <>
        {variant === 'page' ? (
          <div className="mb-3">
            <h4 className="mb-1">راه‌اندازی چت AI</h4>
            <p className="text-600 fs--1 mb-0">
              هر مرحله را جداگانه تکمیل کنید؛ مراحل تکمیل‌شده را می‌توانید رد
              کنید.
            </p>
          </div>
        ) : (
          <p className="text-600 fs--1 mb-3">
            هر مرحله را جداگانه تکمیل کنید؛ مراحل تکمیل‌شده را می‌توانید رد
            کنید.
          </p>
        )}

        <Stepper
          currentStep={activeStep}
          stepStatus={gate.stepStatus}
          visitedSteps={{
            1: Boolean(visitedSteps[1]),
            2: Boolean(visitedSteps[2]),
            3: Boolean(visitedSteps[3])
          }}
          onSelectStep={focusStep}
        />

        {activeStep === 1 ? (
          <ConsentStep
            accepted={gate.consentAccepted}
            onAccept={async () => {
              await gate.acceptConsent();
              focusStep(2);
            }}
            onSkip={
              gate.stepStatus[1] ? () => goToNextIncomplete(1) : undefined
            }
          />
        ) : null}

        {activeStep === 2 ? (
          <ApiKeyStep
            hasApiKeyReady={gate.hasApiKeyReady}
            hasActiveApiKey={gate.hasActiveApiKey}
            enableUserAi={gate.enableUserAi}
            activeProviderKeys={gate.activeProviderKeys}
            onRefresh={handleRefresh}
            isRefreshing={
              isRefreshing || gate.isLoading || gate.isVerifyingKeys
            }
            onSkip={() => goToNextIncomplete(2)}
          />
        ) : null}

        {activeStep === 3 ? (
          <WalletStep
            hasAiWalletBalance={gate.hasAiWalletBalance}
            aiBalance={gate.aiBalance}
            domainSlug={gate.domain_slug}
            canEnterChat={gate.consentAccepted}
            onEnterChat={() => {
              void gate.enterChat({ navigateToChat: onEnterChatNavigate });
            }}
          />
        ) : null}
    </>
  );

  if (variant === 'modal') {
    return content;
  }

  return (
    <Row className="justify-content-center py-3">
      <Col xs={12} lg={9} xl={8}>{content}</Col>
    </Row>
  );
};

export default McpChatOnboarding;
